import { Breadcrumb, Container, ContainerSize, Heading, Text } from '@rebootui';
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

type BreadcrumbPageProps = {}

const BreadcrumbPage = ({ }: BreadcrumbPageProps) => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Breadcrumb
            </Heading>
            <Text el="p"><Text el="strong">Breadcrumbs</Text> indicate the current page’s location within a navigational hierarchy. They are commonly used for page navigation and showing the user’s path.</Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Example
            </Heading>
            <Text className="mb-4">
                The <Text el="strong">Breadcrumb</Text> component displays a navigation trail using <Text el="strong">Breadcrumb.Item</Text> elements. Each item can be a link or the current page.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Breadcrumb>
                        <Breadcrumb.Item path="../">Home</Breadcrumb.Item>
                        <Breadcrumb.Item path="../">Component </Breadcrumb.Item>
                        <Breadcrumb.Item isCurrent={true} >Badges </Breadcrumb.Item>
                    </Breadcrumb>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {BreadcrumbDefaultCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Dividers
            </Heading>
            <Text className="mb-4">
                By default, <Text el="strong">breadcrumb</Text> items are separated using a standard divider (commonly a slash /). If you want to customize this separator to better match your design or layout needs, you can use the <Text el="strong">divider</Text> prop on the Breadcrumb component.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Breadcrumb divider="<">
                        <Breadcrumb.Item path="../">Home</Breadcrumb.Item>
                        <Breadcrumb.Item path="../">Component </Breadcrumb.Item>
                        <Breadcrumb.Item isCurrent={true}> Badges </Breadcrumb.Item>
                    </Breadcrumb>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {BreadcrumbDividerCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Removing Dividers
            </Heading>
            <Text className="mb-4">
                If you don’t want any separator between <Text el="strong">breadcrumb</Text> items, you can pass an <Text el="strong">empty string ("")</Text> to the <Text el="strong">divider</Text> prop. This removes the visual symbol between items and displays them directly next to each other.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Breadcrumb divider="">
                        <Breadcrumb.Item path="../">Home</Breadcrumb.Item>
                        <Breadcrumb.Item path="../">Component </Breadcrumb.Item>
                        <Breadcrumb.Item isCurrent={true}> Badges </Breadcrumb.Item>
                    </Breadcrumb>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {BreadcrumbWithoutDividerCode}
                </CodeHighlighter>
            </CodeSnippet>

        </Container>
    )
}

export default BreadcrumbPage;

const BreadcrumbDefaultCode = `<Breadcrumb>
    <Breadcrumb.Item path="../">Home</Breadcrumb.Item>
    <Breadcrumb.Item path="../">Component </Breadcrumb.Item>
    <Breadcrumb.Item isCurrent={true}> Badges </Breadcrumb.Item>
</Breadcrumb>`;

const BreadcrumbDividerCode = `<Breadcrumb divider="<">
    <Breadcrumb.Item path="../">Home</Breadcrumb.Item>
    <Breadcrumb.Item path="../">Component </Breadcrumb.Item>
    <Breadcrumb.Item isCurrent={true}> Badges </Breadcrumb.Item>
</Breadcrumb>`;

const BreadcrumbWithoutDividerCode = `<Breadcrumb divider="">
    <Breadcrumb.Item path="../">Home</Breadcrumb.Item>
    <Breadcrumb.Item path="../">Component </Breadcrumb.Item>
    <Breadcrumb.Item isCurrent={true}> Badges </Breadcrumb.Item>
</Breadcrumb>`;
